import { PublicClientApplication, type AccountInfo } from '@azure/msal-browser';

import { countryCodeToName } from './countries';

interface AdminConfigResponse {
  enabled: boolean;
  tenantId?: string;
  clientId?: string;
  adminOrigin?: string;
}

interface AdminIdentity {
  email: string;
  displayName?: string;
}

interface AdminReservation {
  reservationKey: string;
  channel: string;
  checkInDate: string;
  checkOutDate: string;
  registrationSubmittedAt: string;
  numberOfGuests?: number;
  leadGuestName?: string;
  contactEmail?: string;
  nationality?: string;
}

interface FullRegistrationGuest {
  lastName: string;
  firstNames: string;
  socialIdNumber: string;
  nationality: string;
  address: string;
  nationalityCode?: string;
  documentNumber?: string;
  documentType?: 'passport' | 'idCard';
  countryOfOrigin?: string;
  countryOfOriginCode?: string;
}

interface AdminRegistrationsResponse {
  admin: AdminIdentity;
  registrations: AdminReservation[];
}

interface FullRegistrationDetails {
  email: string;
  phoneNumber: string;
  numberOfGuests: number;
  guests: FullRegistrationGuest[];
  confirmedCorrectness: true;
}

interface AdminRegistrationDetail {
  reservationKey: string;
  channel: string;
  checkInDate: string;
  checkOutDate: string;
  registrationSubmittedAt: string;
  sourceLabel?: string;
  sourceUrl?: string;
  summary?: string;
  description?: string;
  registrationObjectKey: string;
  registration: FullRegistrationDetails;
}

interface AdminRegistrationDetailResponse {
  admin: AdminIdentity;
  registration: AdminRegistrationDetail;
}

interface AdminRegistrationDeleteResponse {
  success: boolean;
  reservationKey: string;
}

interface ApiErrorResponse {
  code?: string;
  message?: string;
}

class ApiRequestError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;

  public constructor(status: number, payload: ApiErrorResponse) {
    super(payload.message ?? 'Request failed.');
    this.status = status;
    this.code = payload.code;
  }
}

const loginButton = document.querySelector<HTMLButtonElement>('#admin-login');
const logoutButton = document.querySelector<HTMLButtonElement>('#admin-logout');
const refreshButton = document.querySelector<HTMLButtonElement>('#admin-refresh');
const statusBanner = document.querySelector<HTMLElement>('#admin-status');
const accountCard = document.querySelector<HTMLElement>('#admin-account-card');
const accountEmail = document.querySelector<HTMLElement>('#admin-account-email');
const toolbar = document.querySelector<HTMLElement>('#admin-toolbar');
const tablePanel = document.querySelector<HTMLElement>('#admin-table-panel');
const tableBody = document.querySelector<HTMLTableSectionElement>('#admin-table-body');
const emptyState = document.querySelector<HTMLElement>('#admin-empty-state');
const detailPanel = document.querySelector<HTMLElement>('#admin-detail-panel');
const detailBody = document.querySelector<HTMLElement>('#admin-detail-body');
const detailSubtitle = document.querySelector<HTMLElement>('#admin-detail-subtitle');
const resetSelectedButton = document.querySelector<HTMLButtonElement>('#admin-reset-selected');

const tokenStorageKey = 'bwob-admin-id-token';

let authClient: PublicClientApplication | undefined;
let authConfig: AdminConfigResponse | undefined;
let idToken: string | undefined;
let registrationsCache: AdminReservation[] = [];
let selectedReservationKey: string | undefined;

function setStatus(message: string, tone: 'error' | 'success' | 'info'): void {
  if (!statusBanner) {
    return;
  }

  statusBanner.textContent = message;
  statusBanner.classList.remove('hidden', 'error', 'success', 'info');
  statusBanner.classList.add(tone);
}

function clearStatus(): void {
  if (!statusBanner) {
    return;
  }

  statusBanner.classList.add('hidden');
  statusBanner.textContent = '';
  statusBanner.classList.remove('error', 'success', 'info');
}

function setSignedInState(account: AccountInfo | undefined): void {
  const email = account?.username;

  if (accountCard && accountEmail) {
    if (email) {
      accountEmail.textContent = email;
      accountCard.classList.remove('hidden');
    } else {
      accountEmail.textContent = '';
      accountCard.classList.add('hidden');
    }
  }

  loginButton?.classList.toggle('hidden', Boolean(email));
  logoutButton?.classList.toggle('hidden', !email);
  toolbar?.classList.toggle('hidden', !email);
}

function setSignedInEmail(email: string, displayName?: string): void {
  setSignedInState(undefined);

  if (accountCard && accountEmail) {
    accountEmail.textContent = displayName ? `${displayName} (${email})` : email;
    accountCard.classList.remove('hidden');
  }

  loginButton?.classList.add('hidden');
  logoutButton?.classList.remove('hidden');
  toolbar?.classList.remove('hidden');
}

function setTableRows(registrations: AdminReservation[]): void {
  if (!tableBody || !tablePanel || !emptyState) {
    return;
  }

  tableBody.innerHTML = '';

  if (registrations.length === 0) {
    tablePanel.classList.add('hidden');
    emptyState.classList.remove('hidden');
    return;
  }

  emptyState.classList.add('hidden');
  tablePanel.classList.remove('hidden');

  for (const registration of registrations) {
    const row = document.createElement('tr');
    if (registration.reservationKey === selectedReservationKey) {
      row.classList.add('selected');
    }

    row.innerHTML = `
      <td>${escapeHtml(formatDateTime(registration.registrationSubmittedAt))}</td>
      <td>${escapeHtml(registration.channel)}</td>
      <td>${escapeHtml(`${registration.checkInDate} to ${registration.checkOutDate}`)}</td>
      <td>${escapeHtml(registration.leadGuestName ?? '-')}</td>
      <td>${escapeHtml(registration.contactEmail ?? '-')}</td>
      <td>${escapeHtml(String(registration.numberOfGuests ?? '-'))}</td>
      <td>${escapeHtml(registration.nationality ?? '-')}</td>
      <td>
        <div class="admin-row-actions">
          <button class="button-secondary admin-table-action" type="button" data-action="view" data-reservation-key="${escapeHtml(registration.reservationKey)}">View details</button>
          <button class="button-secondary admin-table-action admin-table-action-danger" type="button" data-action="reset" data-reservation-key="${escapeHtml(registration.reservationKey)}">Delete details</button>
        </div>
      </td>
    `;
    tableBody.append(row);
  }
}

function clearDetailPanel(): void {
  selectedReservationKey = undefined;
  detailPanel?.classList.add('hidden');
  resetSelectedButton?.classList.add('hidden');
  if (detailSubtitle) {
    detailSubtitle.textContent = 'Select a completed registration to inspect its full details.';
  }
  if (detailBody) {
    detailBody.innerHTML = '';
  }
  setTableRows(registrationsCache);
}

function renderDetailPanel(registration: AdminRegistrationDetail): void {
  if (!detailPanel || !detailBody || !detailSubtitle || !resetSelectedButton) {
    return;
  }

  selectedReservationKey = registration.reservationKey;
  detailPanel.classList.remove('hidden');
  resetSelectedButton.classList.remove('hidden');
  detailSubtitle.textContent = `${registration.channel} stay from ${registration.checkInDate} to ${registration.checkOutDate}`;
  resetSelectedButton.dataset.reservationKey = registration.reservationKey;

  const details: Array<[string, string]> = [
    ['Submitted', formatDateTime(registration.registrationSubmittedAt)],
    ['Channel', registration.channel],
    ['Check-in', registration.checkInDate],
    ['Check-out', registration.checkOutDate],
    ['Contact email', registration.registration.email],
    ['Contact phone number', registration.registration.phoneNumber],
    ['Guests', String(registration.registration.numberOfGuests)],
    ['Confirmed correctness', registration.registration.confirmedCorrectness ? 'Yes' : 'No'],
    ['Source label', registration.sourceLabel ?? '-'],
    ['Source URL', registration.sourceUrl ?? '-'],
    ['Imported summary', registration.summary ?? '-'],
    ['Imported description', registration.description ?? '-'],
    ['Storage key', registration.registrationObjectKey],
  ];

  const summaryHtml = details
    .map(
      ([label, value]) => `
        <div class="admin-detail-item">
          <p class="admin-detail-label">${escapeHtml(label)}</p>
          <p class="admin-detail-value">${escapeHtml(value)}</p>
        </div>
      `,
    )
    .join('');

  const guestHtml = registration.registration.guests
    .map(
      (guest, index) => `
        <section class="admin-guest-card">
          <h3 class="admin-guest-title">Guest ${index + 1}</h3>
          <div class="admin-guest-grid">
            <div class="admin-detail-item">
              <p class="admin-detail-label">Last name</p>
              <p class="admin-detail-value">${escapeHtml(guest.lastName)}</p>
            </div>
            <div class="admin-detail-item">
              <p class="admin-detail-label">First names</p>
              <p class="admin-detail-value">${escapeHtml(guest.firstNames)}</p>
            </div>
            <div class="admin-detail-item">
              <p class="admin-detail-label">Social ID number</p>
              <p class="admin-detail-value">${escapeHtml(guest.socialIdNumber)}</p>
            </div>
            <div class="admin-detail-item">
              <p class="admin-detail-label">Nationality</p>
              <p class="admin-detail-value">${escapeHtml(
                guest.nationalityCode
                  ? countryCodeToName(guest.nationalityCode, navigator.language)
                  : guest.nationality,
              )}</p>
            </div>
            <div class="admin-detail-item admin-detail-item-wide">
              <p class="admin-detail-label">Address</p>
              <p class="admin-detail-value">${escapeHtml(guest.address)}</p>
            </div>
            <div class="admin-detail-item">
              <p class="admin-detail-label">Passport or ID card number</p>
              <p class="admin-detail-value">${escapeHtml(guest.documentNumber ?? '-')}</p>
            </div>
            <div class="admin-detail-item">
              <p class="admin-detail-label">Document type</p>
              <p class="admin-detail-value">${escapeHtml(
                guest.documentType === 'passport'
                  ? 'Passport'
                  : guest.documentType === 'idCard'
                    ? 'ID card'
                    : '-',
              )}</p>
            </div>
            <div class="admin-detail-item">
              <p class="admin-detail-label">Country of origin</p>
              <p class="admin-detail-value">${escapeHtml(
                guest.countryOfOriginCode
                  ? countryCodeToName(guest.countryOfOriginCode, navigator.language)
                  : guest.countryOfOrigin ?? '-',
              )}</p>
            </div>
          </div>
        </section>
      `,
    )
    .join('');

  detailBody.innerHTML = `${summaryHtml}<div class="admin-guest-list">${guestHtml}</div>`;

  setTableRows(registrationsCache);
}

function formatDateTime(input: string): string {
  const value = new Date(input);
  if (Number.isNaN(value.getTime())) {
    return input;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(value);
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = (await response.json()) as T | ApiErrorResponse;
  if (!response.ok) {
    throw new ApiRequestError(response.status, payload as ApiErrorResponse);
  }

  return payload as T;
}

async function loadAdminConfig(): Promise<AdminConfigResponse> {
  const response = await fetch('/api/admin/config', {
    headers: {
      accept: 'application/json',
    },
  });

  return parseResponse<AdminConfigResponse>(response);
}

async function ensureAuthClient(): Promise<PublicClientApplication> {
  if (authClient) {
    return authClient;
  }

  authConfig = await loadAdminConfig();
  if (!authConfig.enabled || !authConfig.clientId) {
    throw new Error('Admin authentication is not configured yet. Add the Microsoft Entra app client ID in the deployment config.');
  }

  if (!authConfig.adminOrigin) {
    throw new Error('Admin origin is not configured.');
  }

  if (window.location.origin !== authConfig.adminOrigin) {
      window.location.replace(`${authConfig.adminOrigin}/`);
    throw new Error('Redirecting to the canonical admin URL.');
  }

  authClient = new PublicClientApplication({
    auth: {
      clientId: authConfig.clientId,
      authority: `https://login.microsoftonline.com/${authConfig.tenantId ?? 'common'}`,
      redirectUri: `${authConfig.adminOrigin}/`,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
  });

  await authClient.initialize();
  const redirectResult = await authClient.handleRedirectPromise();
  const resolvedAccount = redirectResult?.account ?? authClient.getAllAccounts()[0];

  if (resolvedAccount) {
    authClient.setActiveAccount(resolvedAccount);
  }

  if (redirectResult?.idToken) {
    sessionStorage.setItem(tokenStorageKey, redirectResult.idToken);
  }

  setSignedInState(resolvedAccount);
  idToken = redirectResult?.idToken ?? sessionStorage.getItem(tokenStorageKey) ?? undefined;
  return authClient;
}

async function login(): Promise<void> {
  clearStatus();

  try {
    const client = await ensureAuthClient();
    setStatus('Redirecting to Microsoft sign-in...', 'info');
    await client.loginRedirect({
      scopes: ['openid', 'profile', 'email'],
      prompt: 'select_account',
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Microsoft sign-in failed.';
    setStatus(message, 'error');
  }
}

async function logout(): Promise<void> {
  sessionStorage.removeItem(tokenStorageKey);
  idToken = undefined;
  setSignedInState(undefined);
  setTableRows([]);
  tablePanel?.classList.add('hidden');
  emptyState?.classList.add('hidden');

  if (authClient) {
    await authClient.logoutRedirect({
      postLogoutRedirectUri: `${authConfig?.adminOrigin ?? window.location.origin}/`,
    });
    return;
  }

  setStatus('Signed out.', 'info');
}

async function loadRegistrations(): Promise<void> {
  if (!idToken) {
    setStatus('Sign in with Microsoft first.', 'error');
    return;
  }

  setStatus('Loading completed registrations...', 'info');

  try {
    const response = await fetch('/api/admin/registrations?limit=100', {
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${idToken}`,
      },
    });

    const payload = await parseResponse<AdminRegistrationsResponse>(response);
    registrationsCache = payload.registrations;
    setSignedInEmail(payload.admin.email, payload.admin.displayName);
    setTableRows(registrationsCache);

    if (selectedReservationKey && !registrationsCache.some((item) => item.reservationKey === selectedReservationKey)) {
      clearDetailPanel();
    }

    setStatus('Completed registration list loaded.', 'success');
  } catch (error: unknown) {
    if (error instanceof ApiRequestError && [401, 403].includes(error.status)) {
      sessionStorage.removeItem(tokenStorageKey);
      idToken = undefined;
      setSignedInState(undefined);
    }

    const message = error instanceof Error ? error.message : 'The admin list could not be loaded.';
    setStatus(message, 'error');
  }
}

async function loadRegistrationDetail(reservationKey: string): Promise<void> {
  if (!idToken) {
    setStatus('Sign in with Microsoft first.', 'error');
    return;
  }

  setStatus('Loading registration details...', 'info');

  try {
    const response = await fetch(`/api/admin/registration?reservationKey=${encodeURIComponent(reservationKey)}`, {
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${idToken}`,
      },
    });

    const payload = await parseResponse<AdminRegistrationDetailResponse>(response);
    renderDetailPanel(payload.registration);
    setSignedInEmail(payload.admin.email, payload.admin.displayName);
    setStatus('Registration details loaded.', 'success');
  } catch (error: unknown) {
    if (error instanceof ApiRequestError && [401, 403].includes(error.status)) {
      sessionStorage.removeItem(tokenStorageKey);
      idToken = undefined;
      setSignedInState(undefined);
    }

    const message = error instanceof Error ? error.message : 'The registration details could not be loaded.';
    setStatus(message, 'error');
  }
}

async function resetRegistration(reservationKey: string): Promise<void> {
  if (!idToken) {
    setStatus('Sign in with Microsoft first.', 'error');
    return;
  }

  const confirmed = window.confirm(
    'Delete the stored registration details and allow this reservation to be completed again?',
  );
  if (!confirmed) {
    return;
  }

  setStatus('Deleting registration details...', 'info');

  try {
    const response = await fetch(`/api/admin/registration?reservationKey=${encodeURIComponent(reservationKey)}`, {
      method: 'DELETE',
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${idToken}`,
      },
    });

    await parseResponse<AdminRegistrationDeleteResponse>(response);
    if (selectedReservationKey === reservationKey) {
      clearDetailPanel();
    }
    await loadRegistrations();
    setStatus('Registration details deleted. The guest can submit them again.', 'success');
  } catch (error: unknown) {
    if (error instanceof ApiRequestError && [401, 403].includes(error.status)) {
      sessionStorage.removeItem(tokenStorageKey);
      idToken = undefined;
      setSignedInState(undefined);
    }

    const message = error instanceof Error ? error.message : 'The registration details could not be deleted.';
    setStatus(message, 'error');
  }
}

loginButton?.addEventListener('click', () => {
  void login();
});

logoutButton?.addEventListener('click', () => {
  void logout();
});

refreshButton?.addEventListener('click', () => {
  void loadRegistrations();
});

tableBody?.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const button = target.closest<HTMLButtonElement>('button[data-action][data-reservation-key]');
  if (!button) {
    return;
  }

  const reservationKey = button.dataset.reservationKey;
  if (!reservationKey) {
    return;
  }

  if (button.dataset.action === 'view') {
    void loadRegistrationDetail(reservationKey);
  }

  if (button.dataset.action === 'reset') {
    void resetRegistration(reservationKey);
  }
});

resetSelectedButton?.addEventListener('click', () => {
  const reservationKey = resetSelectedButton.dataset.reservationKey;
  if (!reservationKey) {
    return;
  }

  void resetRegistration(reservationKey);
});

void ensureAuthClient()
  .then(() => {
    if (idToken) {
      void loadRegistrations();
    } else if (!authConfig?.enabled) {
      setStatus('Admin authentication is not configured yet. Add the Microsoft Entra app client ID in the deployment config.', 'error');
    } else {
      setStatus('Sign in with Microsoft to review completed registrations.', 'info');
    }
  })
  .catch((error: unknown) => {
    const message = error instanceof Error ? error.message : 'The admin UI could not be initialised.';
    setStatus(message, 'error');
  });